import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Toast from './Toast';
import { QuickAddVendorModal } from './QuickAddModal';

/**
 * ExtractionProgress - Animated multi-step progress bar shown during OCR extraction.
 *
 * Displays estimated progress phases with timed transitions since the extraction
 * is a single API call and real progress cannot be tracked. Each step advances
 * every 4 seconds to give the user visual feedback.
 *
 * @component
 */
function ExtractionProgress({ onProgress }) {
  const [step, setStep] = useState(0);
  const steps = [
    { label: 'Reading invoice pages...', pct: 5 },
    { label: 'Running local OCR on page content...', pct: 12 },
    { label: 'Analyzing document structure...', pct: 20 },
    { label: 'Identifying table columns and headers...', pct: 28 },
    { label: 'Extracting line items...', pct: 36 },
    { label: 'Reading quantities and prices...', pct: 44 },
    { label: 'Extracting totals and subtotals...', pct: 52 },
    { label: 'Identifying categories...', pct: 60 },
    { label: 'Matching vendor information...', pct: 66 },
    { label: 'Validating quantities against totals...', pct: 72 },
    { label: 'Cross-checking subtotal...', pct: 78 },
    { label: 'Reviewing extraction accuracy...', pct: 84 },
    { label: 'Preparing results...', pct: 90 },
    { label: 'Almost done...', pct: 94 },
    { label: 'Finalizing...', pct: 97 },
  ];

  useEffect(() => {
    const timers = steps.map((_, i) => {
      if (i === 0) return null;
      return setTimeout(() => setStep(i), i * 4500);
    });
    return () => timers.forEach(t => t && clearTimeout(t));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = steps[step];

  useEffect(() => {
    if (onProgress) onProgress(current.pct);
  }, [current.pct, onProgress]);

  return (
    <div className="prog-wrap ocr-upload__progress">
      <div className="prog-head">
        <span className="prog-lbl">{current.label}</span>
        <span className="prog-pct">{current.pct}%</span>
      </div>
      <div className="prog-track">
        <div className="prog-fill" style={{ width: `${current.pct}%` }} />
      </div>
    </div>
  );
}

export { ExtractionProgress };

/**
 * ClaudeOCRUpload - Upload PDF and extract invoice data using COGSLOG.
 *
 * Handles the upload → vendor detection → extraction flow. On successful extraction,
 * passes the structured data to the parent via onExtractComplete.
 *
 * @param {Object} props
 * @param {Function} props.onExtractComplete - Callback({extractedData, filepath, previewImage})
 * @param {Function} [props.onError] - Callback(errorMessage)
 * @component
 */
function ClaudeOCRUpload({ onExtractComplete, onError }) {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [vendorResult, setVendorResult] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  /**
   * Load vendor list for manual selection fallback.
   */
  const loadVendors = useCallback(async () => {
    try {
      const response = await api.getVendors();
      setVendors(response.data.filter(v => v.status === 'active'));
    } catch (err) {
      console.error('Failed to load vendors:', err);
    }
  }, []);

  /**
   * Handle file selection (drag-drop or click).
   * @param {File} file - The PDF file to process
   */
  const handleFile = useCallback(async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      const msg = 'Please upload a PDF file';
      setToast({ message: msg, type: 'error' });
      if (onError) onError(msg);
      return;
    }

    setToast(null);
    setUploading(true);
    setUploadResult(null);
    setVendorResult(null);

    try {
      // Step 1: Upload file
      setToast({ message: 'Uploading PDF...', type: 'info' });
      const uploadResponse = await api.uploadForVision(file);
      const upload = uploadResponse.data;
      setUploadResult(upload);
      setToast({ message: 'Identifying vendor...', type: 'info' });

      // Step 2: Load vendors + identify vendor in parallel
      const [, vendorIdResult] = await Promise.allSettled([
        loadVendors(),
        api.identifyVendor(upload.filepath),
      ]);

      if (vendorIdResult.status === 'fulfilled') {
        const vendor = vendorIdResult.value.data;
        setVendorResult(vendor);

        if (vendor.vendor_id && vendor.confidence >= 0.7) {
          setSelectedVendorId(String(vendor.vendor_id));
          setToast({ message: `Vendor detected: ${vendor.matched_name} (${Math.round(vendor.confidence * 100)}% confidence)`, type: 'success' });
        } else {
          setToast({ message: 'Vendor not detected. Please select one manually.', type: 'warning' });
        }
      } else {
        setToast({ message: 'Could not identify vendor. Please select manually.', type: 'warning' });
      }

      setUploading(false);
    } catch (err) {
      const msg = 'Upload failed. Please try again.';
      setToast({ message: msg, type: 'error' });
      setUploading(false);
      if (onError) onError(msg);
    }
  }, [onError, loadVendors]);

  /**
   * Run COGSLOG extraction on the uploaded PDF.
   */
  /**
   * Run COGSLOG extraction on the uploaded PDF.
   * Validates that a vendor is selected before proceeding.
   */
  const handleExtract = useCallback(async () => {
    if (!uploadResult) return;

    if (!selectedVendorId) {
      setToast({ message: 'Please select a vendor before extracting', type: 'error' });
      return;
    }

    setExtracting(true);
    setToast({ message: 'Extracting invoice data with COGSLOG...', type: 'info' });

    try {
      const response = await api.extractWithVision({
        filepath: uploadResult.filepath,
        vendor_id: selectedVendorId ? parseInt(selectedVendorId) : undefined,
        vendor_name: vendorResult?.matched_name || vendorResult?.vendor_name,
      });

      setExtracting(false);
      setToast({ message: 'Extraction complete!', type: 'success' });

      if (onExtractComplete) {
        onExtractComplete({
          extractedData: response.data,
          filepath: uploadResult.filepath,
          previewImage: uploadResult.preview_image,
          previewPages: uploadResult.preview_pages || [],
          vendorId: selectedVendorId ? parseInt(selectedVendorId) : null,
          vendorName: vendorResult?.matched_name || vendorResult?.vendor_name || '',
        });
      }
    } catch (err) {
      setExtracting(false);
      const msg = 'Extraction failed. Please try again.';
      setToast({ message: msg, type: 'error' });
      if (onError) onError(msg);
    }
  }, [uploadResult, selectedVendorId, vendorResult, onExtractComplete, onError]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleInputChange = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  }, [handleFile]);

  const handleReset = useCallback(() => {
    setUploadResult(null);
    setVendorResult(null);
    setSelectedVendorId('');
    setToast(null);
    setExtracting(false);
    setUploading(false);
  }, []);

  return (
    <div>
      {/* Upload Zone */}
      {!uploadResult && (
        <>
          <div
            className={`ocr-upload__dropzone ${dragActive ? 'ocr-upload__dropzone--active' : ''} ${uploading ? 'ocr-upload__dropzone--uploading' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
            role="button"
            tabIndex={0}
            aria-label="Upload PDF or image for OCR processing"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
              onChange={handleInputChange}
              className="ocr-upload__file-input"
              aria-hidden="true"
            />
            <p className="ocr-upload__dropzone-title">
              {uploading ? 'Uploading...' : 'Drop PDF or image here or click to browse'}
            </p>
            <p className="ocr-upload__dropzone-subtitle">
              COGSLOG will automatically extract all invoice data
            </p>
          </div>

          <div className="batch-link-banner">
            Need to process multiple invoices?{' '}
            <Link to="batch">Go to Batch Processing &rarr;</Link>
          </div>
          <div className="card" style={{ marginTop: 'var(--space-4)', backgroundColor: 'var(--st100)' }}>
            <h3 style={{ margin: '0 0 var(--space-2)', fontSize: '0.95rem' }}>Tips for best results</h3>
            <ul style={{ margin: 0, paddingLeft: 'var(--space-4)', color: 'var(--st600)', fontSize: '0.85rem', lineHeight: '1.6' }}>
              <li>Use clear, high-resolution PDF scans for accurate extraction</li>
              <li>COGSLOG works best with standard invoice formats (line items, totals, dates)</li>
              <li>After extraction, review and edit any fields before saving</li>
              <li>Set up your vendors in <Link to="../setup" style={{ color: 'var(--g500)' }}>Setup</Link> first for automatic vendor detection</li>
            </ul>
          </div>
        </>
      )}

      {/* Toast Notifications */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Upload Result - Preview + Vendor Selection + Extract Button */}
      {uploadResult && !extracting && (
        <div className="ocr-upload__result">
          {/* Left: Preview thumbnail */}
          {uploadResult.preview_image && (
            <div className="ocr-upload__preview-thumb">
              <img
                src={`data:image/png;base64,${uploadResult.preview_image}`}
                alt="PDF Preview"
                className="ocr-upload__preview-img"
              />
            </div>
          )}

          {/* Right: Vendor selection + actions */}
          <div className="ocr-upload__controls">
            <div className="field">
              <label className="label" htmlFor="vendor-select">Vendor</label>
              {vendorResult?.vendor_id && vendorResult.confidence >= 0.7 ? (
                <div className="ocr-upload__vendor-detected">
                  <span className="badge" style={{ color: 'var(--success)' }}>
                    Detected: {vendorResult.matched_name} (<span style={{ fontFamily: 'var(--font-m)' }}>{Math.round(vendorResult.confidence * 100)}%</span>)
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setVendorResult(null);
                      setSelectedVendorId('');
                      loadVendors();
                    }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <select
                  id="vendor-select"
                  className="input"
                  value={selectedVendorId}
                  onChange={(e) => {
                    if (e.target.value === '__add_new__') {
                      setShowVendorModal(true);
                    } else {
                      setSelectedVendorId(e.target.value);
                    }
                  }}
                >
                  <option value="">Select vendor...</option>
                  {vendors.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                  <option value="__add_new__">+ Add New Vendor...</option>
                </select>
              )}
            </div>

            <div className="ocr-upload__actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleExtract}
                disabled={extracting}
              >
                Extract Invoice Data
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleReset}
              >
                Start Over
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extracting indicator with progress bar */}
      {extracting && (
        <div className="ocr-upload__extracting">
          <img src="/logos/loader/cogslog-loader-light-md.svg" width="48" height="108" alt="Loading..." role="status" className="ocr-upload__loader" />
          <ExtractionProgress />
        </div>
      )}

      <QuickAddVendorModal
        isOpen={showVendorModal}
        onClose={() => setShowVendorModal(false)}
        onVendorCreated={(newVendor) => {
          loadVendors();
          setSelectedVendorId(String(newVendor.id));
        }}
      />
    </div>
  );
}

ClaudeOCRUpload.propTypes = {
  /** Callback with extraction result: {extractedData, filepath, previewImage, vendorId, vendorName} */
  onExtractComplete: PropTypes.func.isRequired,
  /** Callback when an error occurs. Receives error message string. */
  onError: PropTypes.func,
};

export default ClaudeOCRUpload;
